// Gezondheid per project voor de projectenlijst — pure logica, zonder React.
//
// Bronnen:
// - /Api/projects/gezondheid: per project alleen fase, start/deadline en uren
//   (verkocht + geschreven), server-side uit Gripp samengevat — geen bedragen,
//   geen extra_data. Ontbreekt de route (404), dan geen uren.
// - Open taken en deliverables met een datum (/api/v1/airtable/deadlines).
//   Items met een afgeronde status tellen niet mee, ook als het vinkje leeg is.
//
// Regel voor het statusbolletje (staat ook in de tooltip — houd die gelijk):
//   Te laat  = een deliverable-deadline is (recent) verstreken, óf de geschreven
//              uren liggen boven het verkochte budget, óf 3+ taken zijn recent te laat.
//   Risico   = 1–2 taken recent te laat, óf ≥ 85% van de uren op, óf een
//              deliverable-deadline binnen 3 dagen.
//   Op schema = er is data en niets van het bovenstaande.
//   Geen data = geen urenbudget én geen gedateerde taken/deliverables.
// "Recent" = de afgelopen 60 dagen. Oudere open items tellen als "oud open":
// vrijwel altijd een vergeten vinkje, geen echte achterstand — we tonen ze wel,
// maar ze kleuren het bolletje niet.

export const RECENT_DAGEN = 60;
export const UREN_RISICO = 0.85;
export const DEADLINE_RISICO_DAGEN = 3;
export const UREN_PER_DAG = 8;

// Gripp-fases die nog lopen (3 = gearchiveerd/gefactureerd, 4 = geannuleerd).
export const GRIPP_LOPENDE_STATUSSEN = [1, 2, 5, 6, 7, 8, 9, 10];

// ── datums ──────────────────────────────────────────────────────────────────
const DAG_MS = 86400000;

// 'YYYY-MM-DD…' → lokale middernacht. Alleen het datumdeel telt: de spiegel
// levert naïeve datetimes en een tijdzone-omrekening zou een dag kunnen schuiven.
export function naarDag(waarde) {
  if (!waarde) return null;
  const s = typeof waarde === 'object' && waarde.date ? waarde.date : String(waarde);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function vandaagDag(nu = new Date()) {
  return new Date(nu.getFullYear(), nu.getMonth(), nu.getDate());
}

export function dagenTussen(van, tot) {
  return Math.round((tot.getTime() - van.getTime()) / DAG_MS);
}

// ── Gripp ───────────────────────────────────────────────────────────────────
// Eenheden die in uren om te rekenen zijn. `amountwritten` is in Gripp altijd in
// uren, ook op een dagregel; het verkochte aantal staat in de eenheid van de regel.
const UUR_EENHEDEN = new Set(['uur', 'uren', 'hour', 'hours', 'u']);
const DAG_EENHEDEN = new Set(['dag', 'dag.', 'dagen', 'dgn', 'day', 'day.', 'days', 'lange draaidag']);
const HALVE_DAG_EENHEDEN = new Set(['halve dag', 'half day']);

export function urenUitRegels(regels) {
  let verkocht = 0;
  let geschreven = 0;
  let metDagen = false;
  (regels || []).forEach((r) => {
    const eenheid = String(r?.unit?.searchname || '').trim().toLowerCase();
    const aantal = Number(r?.amount) || 0;
    if (UUR_EENHEDEN.has(eenheid)) verkocht += aantal;
    else if (DAG_EENHEDEN.has(eenheid)) { verkocht += aantal * UREN_PER_DAG; if (aantal) metDagen = true; }
    else if (HALVE_DAG_EENHEDEN.has(eenheid)) { verkocht += aantal * (UREN_PER_DAG / 2); if (aantal) metDagen = true; }
    geschreven += Number(r?.amountwritten) || 0;
  });
  if (verkocht <= 0) return null; // geen urenbudget → niets tonen, niets verzinnen
  return { verkocht, geschreven, fractie: geschreven / verkocht, metDagen };
}

function parseExtra(extra) {
  if (!extra) return {};
  if (typeof extra === 'object') return extra;
  try { return JSON.parse(extra) || {}; } catch { return {}; }
}

// Eén Gripp-project → het slanke deel dat de lijst nodig heeft.
export function grippSamenvatting(gp) {
  const e = parseExtra(gp?.extra_data);
  return {
    grippId: gp?.gripp_id != null ? String(gp.gripp_id) : '',
    nummer: gp?.number != null ? String(gp.number) : '',
    fase: gp?.status_name || e?.phase?.searchname || '',
    faseId: gp?.status ?? null,
    start: naarDag(e.startdate) || naarDag(gp?.start_date),
    deadline: naarDag(e.deadline) || naarDag(e.deliverydate) || naarDag(e.enddate) || naarDag(gp?.end_date),
    uren: urenUitRegels(e.projectlines),
    bedrijf: gp?.company_name || '',
  };
}

// Eén rij uit /Api/projects/gezondheid → dezelfde vorm als grippSamenvatting.
// De engine levert uren al in uren (verkocht/geschreven); de fractie rekenen we hier.
export function grippUitGezondheid(r) {
  const verkocht = Number(r?.uren?.verkocht) || 0;
  const geschreven = Number(r?.uren?.geschreven) || 0;
  return {
    grippId: r?.grippId != null ? String(r.grippId) : '',
    nummer: r?.nummer != null ? String(r.nummer) : '',
    fase: r?.fase || '',
    faseId: r?.faseId ?? null,
    start: naarDag(r?.start),
    deadline: naarDag(r?.deadline),
    // Een handmatig budget van 0 u is óók een budget (wie schrijft zit erboven).
    uren: verkocht > 0 || r?.uren?.handmatig
      ? { verkocht, geschreven, fractie: urenFractie(verkocht, geschreven), metDagen: !!r?.uren?.metDagen, handmatig: !!r?.uren?.handmatig }
      : null,
    bedrijf: r?.bedrijf || '',
  };
}

// Samenvattingen toevoegen aan een (bestaande) index; geeft een nieuwe index terug.
export function voegToeAanIndex(index, samenvattingen) {
  const opNummer = new Map(index?.opNummer || []);
  const opId = new Map(index?.opId || []);
  (samenvattingen || []).forEach((s) => {
    if (s.nummer) opNummer.set(s.nummer, s);
    if (s.grippId) opId.set(s.grippId, s);
  });
  return { opNummer, opId };
}

// Index op projectnummer én Gripp-id, zodat een Airtable-project via
// Projectnummer of GrippProjectID gevonden wordt.
export function grippIndex(lijst) {
  const opNummer = new Map();
  const opId = new Map();
  (lijst || []).forEach((gp) => {
    const s = grippSamenvatting(gp);
    if (s.nummer) opNummer.set(s.nummer, s);
    if (s.grippId) opId.set(s.grippId, s);
  });
  return { opNummer, opId };
}

export function grippVoorProject(index, fields) {
  if (!index || !fields) return null;
  const nr = fields.Projectnummer != null ? String(fields.Projectnummer) : '';
  const gid = fields.GrippProjectID != null ? String(fields.GrippProjectID) : '';
  return (nr && index.opNummer.get(nr)) || (gid && index.opId.get(gid)) || null;
}

// ── taken & deliverables ────────────────────────────────────────────────────
// Statussen (PrioNr bij taken, Status bij deliverables) die "klaar" betekenen.
// Staat zo'n status erop maar is het afgerond-vinkje leeg, dan is het geen
// achterstand maar een vergeten vinkje.
export const AFGERONDE_ITEM_STATUSSEN = new Set(['Afgerond', 'Opgestuurd', 'Gearchiveerd']);

export function isAfgerondItem(it) {
  const st = Array.isArray(it?.status) ? it.status : [it?.status];
  return st.some((s) => AFGERONDE_ITEM_STATUSSEN.has(String(s || '').trim()));
}

// Items uit /airtable/deadlines → per project gegroepeerd, daysUntil herrekend
// op de lokale datum. Afgeronde items vallen eruit.
export function deadlinesPerProject(items, vandaag = vandaagDag()) {
  const uit = {};
  (items || []).forEach((it) => {
    if (isAfgerondItem(it)) return;
    const pid = it?.projectId;
    const dag = naarDag(it?.deadline);
    if (!pid || !dag) return;
    const start = it.type === 'task' ? naarDag(it.startDate) : null;
    (uit[pid] = uit[pid] || []).push({
      id: it.id, soort: it.type === 'deliverable' ? 'deliverable' : 'taak',
      naam: it.name || '', dag, start, dagenTot: dagenTussen(vandaag, dag),
      deadlineType: it.deadlineType || '',
    });
  });
  return uit;
}

// Resultaten van losse verzoeken (chunks) samenvoegen. Per chunk:
// { ids, items } bij succes, { ids, items: null } bij een fout.
// - geslaagde ids zonder open items krijgen [] ("niets open");
// - ids uit een mislukte chunk krijgen GEEN waarde ("onbekend") en staan in
//   `mislukt`, zodat ze opnieuw opgevraagd kunnen worden.
export function voegChunksSamen(chunks, vandaag = vandaagDag()) {
  const perProject = {};
  const mislukt = [];
  (chunks || []).forEach(({ ids, items }) => {
    if (!Array.isArray(items)) { mislukt.push(...(ids || [])); return; }
    const d = deadlinesPerProject(items, vandaag);
    (ids || []).forEach((id) => { perProject[id] = d[id] || []; });
  });
  return { perProject, mislukt };
}

// ── gezondheid ──────────────────────────────────────────────────────────────
export const GEZONDHEID = {
  te_laat: { label: 'Te laat', kleur: '#dc2626', rang: 3 },
  risico: { label: 'Risico', kleur: '#f59e0b', rang: 2 },
  op_schema: { label: 'Op schema', kleur: '#16a34a', rang: 1 },
  onbekend: { label: 'Geen data', kleur: '#cbd5e1', rang: 0 },
  nvt: { label: 'Nog geen project', kleur: '#e2e8f0', rang: -1 },
  afgesloten: { label: 'Afgesloten', kleur: '#e2e8f0', rang: -1 },
};

export const AFGESLOTEN_STATUSSEN = ['Afgerond', 'Gearchiveerd', 'Geannuleerd'];
export const LEAD_STATUSSEN = ['Lead', 'Offerte'];

// `takenOnbekend`: de taken/deliverables konden niet worden opgehaald. Dan is
// "niets te laat" geen oordeel: zonder andere signalen wordt het 'onbekend',
// niet 'op schema'.
export function berekenGezondheid({ gripp = null, items = [], vandaag = vandaagDag(), isLead = false, afgesloten = false, takenOnbekend = false } = {}) {
  const taken = items.filter((i) => i.soort === 'taak');
  const deliverables = items.filter((i) => i.soort === 'deliverable');
  const recentTeLaat = (i) => i.dagenTot < 0 && i.dagenTot >= -RECENT_DAGEN;
  const oud = (i) => i.dagenTot < -RECENT_DAGEN;

  const takenTeLaat = taken.filter(recentTeLaat).length;
  const takenOud = taken.filter(oud).length;
  const deliverablesTeLaat = deliverables.filter(recentTeLaat).length;
  const openTaken = taken.length;

  const toekomst = items.filter((i) => i.dagenTot >= 0).sort((a, b) => a.dagenTot - b.dagenTot);
  const volgende = toekomst[0] || null;
  const volgendeDeliverable = toekomst.find((i) => i.soort === 'deliverable') || null;

  // Periode: start uit Gripp (anders vroegste taakstart), eind = laatste
  // deliverable-deadline of de Gripp-deadline (anders de laatste taak).
  const starts = [gripp?.start, ...taken.map((t) => t.start)].filter(Boolean);
  const start = gripp?.start || (starts.length ? new Date(Math.min(...starts.map((d) => d.getTime()))) : null);
  const eindKandidaten = [gripp?.deadline, ...deliverables.map((d) => d.dag)].filter(Boolean);
  const eindBron = eindKandidaten.length ? eindKandidaten : taken.map((t) => t.dag);
  const eind = eindBron.length ? new Date(Math.max(...eindBron.map((d) => d.getTime()))) : null;
  const mijlpalen = deliverables
    .filter((d) => d.dagenTot >= -RECENT_DAGEN)
    .map((d) => ({ dag: d.dag, naam: d.naam, teLaat: d.dagenTot < 0 }));

  const uren = gripp?.uren || null;
  const redenen = [];
  let status = 'op_schema';

  if (isLead) {
    status = 'nvt';
  } else if (afgesloten) {
    status = 'afgesloten';
  } else {
    if (deliverablesTeLaat) redenen.push({ ernst: 'te_laat', tekst: `${deliverablesTeLaat} deliverable-deadline${deliverablesTeLaat > 1 ? 's' : ''} verstreken` });
    if (uren && uren.fractie > 1) redenen.push({ ernst: 'te_laat', tekst: `Uren over budget (${urenPctTekst(uren.fractie)})` });
    if (takenTeLaat >= 3) redenen.push({ ernst: 'te_laat', tekst: `${takenTeLaat} taken te laat` });
    else if (takenTeLaat > 0) redenen.push({ ernst: 'risico', tekst: `${takenTeLaat} ${takenTeLaat === 1 ? 'taak' : 'taken'} te laat` });
    if (uren && uren.fractie >= UREN_RISICO && uren.fractie <= 1) redenen.push({ ernst: 'risico', tekst: `${Math.round(uren.fractie * 100)}% van de uren op` });
    if (volgendeDeliverable && volgendeDeliverable.dagenTot <= DEADLINE_RISICO_DAGEN) {
      redenen.push({ ernst: 'risico', tekst: `Deliverable-deadline ${volgendeDeliverable.dagenTot === 0 ? 'vandaag' : `over ${volgendeDeliverable.dagenTot} d`}` });
    }
    if (redenen.some((r) => r.ernst === 'te_laat')) status = 'te_laat';
    else if (redenen.length) status = 'risico';
    else if (takenOnbekend) status = 'onbekend';
    else if (!uren && !items.some((i) => i.dagenTot >= -RECENT_DAGEN)) status = 'onbekend';
  }

  return {
    status, redenen, takenTeLaat, takenOud, deliverablesTeLaat, openTaken,
    volgende, start, eind, mijlpalen, uren,
    eindOver: eind ? dagenTussen(vandaag, eind) : null,
    takenOnbekend: !!takenOnbekend,
    fase: gripp?.fase || '',
  };
}

// Eén signaal per rij (rustige lijst): de zwaarste reden, plus hoeveel er nog
// meer zijn. Gezonde projecten en projecten zonder data geven null → stil.
export function signaalVan(g) {
  if (!g || (g.status !== 'te_laat' && g.status !== 'risico')) return null;
  const redenen = g.redenen || [];
  const hoofd = redenen.find((r) => r.ernst === g.status) || redenen[0];
  if (!hoofd) return null;
  return { ernst: g.status, tekst: hoofd.tekst, meer: redenen.length - 1 };
}

// Risicovolgorde: rood → oranje → groen → geen data → leads; binnen een kleur
// eerst de meeste achterstand, dan het hoogste urenverbruik, dan de eerstvolgende deadline.
export function vergelijkRisico(a, b) {
  const ga = a || {}; const gb = b || {};
  const rang = (GEZONDHEID[gb.status]?.rang ?? -2) - (GEZONDHEID[ga.status]?.rang ?? -2);
  if (rang) return rang;
  const achter = ((gb.takenTeLaat || 0) + 5 * (gb.deliverablesTeLaat || 0)) - ((ga.takenTeLaat || 0) + 5 * (ga.deliverablesTeLaat || 0));
  if (achter) return achter;
  const uren = (gb.uren?.fractie || 0) - (ga.uren?.fractie || 0);
  if (Math.abs(uren) > 0.001) return uren;
  const da = ga.volgende ? ga.volgende.dagenTot : Infinity;
  const db = gb.volgende ? gb.volgende.dagenTot : Infinity;
  if (da !== db) return da < db ? -1 : 1;
  return 0;
}

// ── weergave-hulpjes ────────────────────────────────────────────────────────
const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
export function korteDatum(d, vandaag = vandaagDag()) {
  if (!d) return '';
  const jaar = d.getFullYear() !== vandaag.getFullYear() ? ` '${String(d.getFullYear()).slice(2)}` : '';
  return `${d.getDate()} ${MAANDEN[d.getMonth()]}${jaar}`;
}

export function relatief(dagen) {
  if (dagen == null) return '';
  if (dagen === 0) return 'vandaag';
  if (dagen === 1) return 'morgen';
  if (dagen === -1) return 'gisteren';
  return dagen > 0 ? `over ${dagen} d` : `${-dagen} d te laat`;
}

// Kleur van de urenbalk (projectenlijst én projectpagina), zelfde grens als de
// engine (services/project_budget.py): groen < 85 % ≤ oranje ≤ 100 % < rood.
// Fractie geschreven/begroot. Bij 0 u begroot (handmatig): niets geschreven = 0,
// wel geschreven = Infinity (altijd rood, balk vol).
export function urenFractie(begroot, geschreven) {
  const b = Number(begroot) || 0;
  const g = Number(geschreven) || 0;
  if (b > 0) return g / b;
  return g > 0 ? Infinity : 0;
}

export function urenPctTekst(fractie) {
  return Number.isFinite(fractie) ? `${Math.round(fractie * 100)}%` : '0 u begroot';
}

export function urenKleur(fractie) {
  if (fractie == null || Number.isNaN(Number(fractie))) return null;
  if (fractie > 1) return 'rood';
  if (fractie >= UREN_RISICO) return 'oranje';
  return 'groen';
}

export function formatUren(u) {
  const r = Math.round(u * 10) / 10;
  return Number.isInteger(r) ? String(r) : String(r).replace('.', ',');
}

// "9714. Hazera Seeds B.V. - International Open Days content" → klant uit de
// vaste naamconventie (nummer. Klant - Projectnaam). Valt terug op het klantveld.
export function klantVanProject(fields) {
  const naam = String(fields?.Projectnaam || '').trim();
  const volledig = String(fields?.Projectnr_en_naam || '').trim();
  const zonderNr = volledig.replace(/^[^.]*\.\s*/, '');
  if (naam && zonderNr.endsWith(naam)) {
    const klant = zonderNr.slice(0, zonderNr.length - naam.length).replace(/\s*-\s*$/, '').trim();
    if (klant) return klant;
  }
  const raw = fields?.KlantnaamString || fields?.Klantnaam || '';
  const c = Array.isArray(raw) ? raw[0] : raw;
  return c && !String(c).startsWith('rec') ? String(c) : '';
}
